import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import { trainingCatalog } from './trainingsData';
import useTrainingProgress from './hooks/useTrainingProgress';

const STATUS_PILL_CLASSES = {
  completed: 'bg-emerald-100 text-emerald-700',
  inProgress: 'bg-amber-100 text-amber-700',
  notStarted: 'bg-slate-100 text-slate-600',
};

const TrainingsPage = () => {
  const navigate = useNavigate();
  const { progress } = useTrainingProgress();
  const { t } = useTranslation(['header', 'common', 'trainings']);

  const featuredTraining = useMemo(() => trainingCatalog[0], []);
  const notifications = useMemo(() => ({
    messages: 0,
    timeOff: 0,
  }), []);

  const getProgressMeta = useCallback(
    (trainingId, totalModules) => {
      const entry = progress?.[trainingId];
      const completedModules = Array.isArray(entry?.completedModules)
        ? entry.completedModules.length
        : 0;
      const percent = totalModules
        ? Math.round((completedModules / totalModules) * 100)
        : 0;
      let statusKey = 'notStarted';
      if (percent >= 100) {
        statusKey = 'completed';
      } else if (percent > 0) {
        statusKey = 'inProgress';
      }
      return {
        completedModules,
        percent,
        statusKey,
      };
    },
    [progress],
  );

  const handleNavigateToTraining = (trainingId) => {
    navigate(`/trainings/${trainingId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        allowLocationSelection={false}
        notifications={notifications}
      />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
          <section className="bg-gradient-to-br from-emerald-500 via-teal-500 to-sky-500 text-white rounded-3xl p-8 shadow-xl relative overflow-hidden">
            <div className="relative z-10">
              <p className="uppercase tracking-widest text-xs font-semibold opacity-80 mb-2">
                {t('trainings.hero.tagline')}
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold mb-4">
                {t('trainings.hero.title')}
              </h1>
              <p className="text-white/80 text-lg max-w-2xl mb-6">
                {t('trainings.hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  iconName="Play"
                  onClick={() => handleNavigateToTraining(featuredTraining.id)}
                >
                  {formatTranslation(t('trainings.hero.resumeButton'), {
                    title: featuredTraining.title,
                  })}
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  iconName="CheckCircle"
                  onClick={() => navigate('/incident-reports')}
                >
                  {t('trainings.hero.policyButton')}
                </Button>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/3 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/diagmonds.png')] bg-repeat" />
          </section>

          <section>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">{t('trainings.catalog.title')}</h2>
                <p className="text-muted-foreground">{t('trainings.catalog.subtitle')}</p>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Icon name="Sparkles" className="text-primary" size={18} />
                <span>{t('trainings.catalog.newModules')}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {trainingCatalog.map((training) => {
                const totalModules = training.videos.length;
                const { completedModules, percent, statusKey } = getProgressMeta(
                  training.id,
                  totalModules,
                );
                const statusLabel = t(`trainings.progress.${statusKey}`);
                const statusClass = STATUS_PILL_CLASSES[statusKey] ?? STATUS_PILL_CLASSES.notStarted;
                const modulesLabel = formatTranslation(t('trainings.labels.modulesCount'), {
                  completed: completedModules,
                  total: totalModules,
                });
                const updatedLabel = formatTranslation(t('trainings.labels.updated'), {
                  date: training.updatedAt,
                });
                const totalTimeLabel = formatTranslation(t('trainings.labels.estimatedTime'), {
                  time: training.estimatedTime,
                });
                return (
                  <article
                    key={training.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNavigateToTraining(training.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleNavigateToTraining(training.id);
                      }
                    }}
                    className="group border border-border rounded-2xl bg-card hover:shadow-xl transition-all p-6 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                  >
                    <div className={`rounded-xl bg-gradient-to-br ${training.accent} text-white p-4 flex items-center justify-between mb-5`}>
                      <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold opacity-80">
                          <Icon name={training.coverIcon} size={16} />
                          <span>{training.category}</span>
                        </div>
                        <h3 className="text-xl font-semibold mt-2">{training.title}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-80">{training.difficulty}</p>
                        <p className="text-xs opacity-75">{updatedLabel}</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      {training.description}
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2 mb-5">
                      {training.learningGoals.map((goal, index) => (
                        <li key={goal} className="flex items-start gap-2">
                          <Icon name="Check" size={16} className="text-emerald-500 mt-0.5" />
                          <span>{goal}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-border pt-4 flex flex-col gap-4">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                            {statusLabel}
                          </span>
                          <span className="text-muted-foreground">{modulesLabel}</span>
                        </div>
                        <span className="font-semibold text-foreground">
                          {percent}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{totalTimeLabel}</span>
                        <Button
                          size="sm"
                          variant="secondary"
                          iconName="Play"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleNavigateToTraining(training.id);
                          }}
                        >
                          {t('trainings.actions.continue')}
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TrainingsPage;
